import { KnownId } from "@fairfleet/geotab";
import { LiveDataRow } from "../models/LiveDataRow";
import { DEFAULT_ODOMETER_DECIMALS } from "@/hooks/userState/useOdometerDecimals";
import { formatNumber } from "@/utilities/formatNumber";

const MAX_DECIMALS = 3;

/** A reading's value as text: strings pass through, numbers are formatted, anything else is empty. */
function formatReadingValue(
  value: number | string,
  language: string,
  useGrouping: boolean,
): string {
  if (typeof value === "string") {
    return value;
  }
  return formatNumber(value, language, {
    precision: MAX_DECIMALS,
    fixed: false,
    useGrouping,
    missing: "",
  });
}

const SYMBOL_BY_UNIT = new Map<string, string>();

/**
 * Shortens Geotab's unit names to what belongs next to a number.
 *
 * A unit arrives either as one of Geotab's enum ids or as the display name from the user's
 * measurement profile, depending on whether that profile covers the diagnostic — so both spellings
 * have to end up as the same symbol. The enum ids come from `KnownId` rather than being typed out,
 * because they are easy to misremember: it is `UnitOfMeasureVoltsId`, not `UnitOfMeasureVoltId`.
 */
function alias(symbol: string, ...names: string[]): void {
  for (const name of names) {
    SYMBOL_BY_UNIT.set(name.toLowerCase(), symbol);
  }
}

alias("%", KnownId.UnitOfMeasurePercentageId, "%", "percent", "percentage");
alias("A", KnownId.UnitOfMeasureAmpsId, "a", "amp", "amps", "ampere");
alias("V", KnownId.UnitOfMeasureVoltsId, "v", "volt", "volts");
alias("W", KnownId.UnitOfMeasureWattsId, "w", "watt", "watts");
alias("rpm", KnownId.UnitOfMeasureRevolutionsPerMinuteId, "rpm", "u/min", "revolutions per minute");
alias("l", KnownId.UnitOfMeasureLitersId, "l", "liter", "liters", "litre", "litres");
alias("gal", "gal", "gallon", "gallons");
alias("m", KnownId.UnitOfMeasureMetersId, "m", "meter", "meters", "metre", "metres");
alias("km", "km", "kilometer", "kilometers", "kilometre", "kilometres");

export function normalizeUnitOfMeasure(unitOfMeasure: string | null | undefined): string {
  if (!unitOfMeasure || unitOfMeasure === KnownId.UnitOfMeasureNoneId) {
    return "";
  }

  const trimmed = unitOfMeasure.trim();
  return SYMBOL_BY_UNIT.get(trimmed.toLowerCase()) ?? unitOfMeasure;
}

/**
 * A reading as one checklist line: the raw value in the language in use, with its unit.
 *
 * No factor/offset conversion is applied — the grid and checklist must always show the same
 * number for the same reading.
 *
 * Not grouped, because a checklist line is read as a single value rather than scanned down a
 * column of figures, and thousands separators only add noise there.
 *
 * @param row - The reading, or `undefined` when the value was never reported.
 * @param language - The language the app is running in.
 */
export function formatChecklistValue(row: LiveDataRow | undefined, language: string): string {
  if (!row) {
    return "";
  }

  const value = formatReadingValue(row.value, language, false);
  if (value === "") {
    // Nothing readable to attach a unit to; a bare " V" would look like a broken row.
    return "";
  }

  const unit = normalizeUnitOfMeasure(row.unitOfMeasure);
  return unit ? `${value} ${unit}` : value;
}

/**
 * An odometer reading as a number, with its raw unit.
 *
 * No factor/offset conversion is applied — the grid and checklist must always show the same
 * number for the same reading.
 *
 * A number rather than a string, because the fleet-wide view sorts and compares these.
 */
export function odometerValue(
  row: LiveDataRow | undefined,
): { value: number; unit: string } | null {
  if (!row || typeof row.value !== "number" || !Number.isFinite(row.value)) {
    return null;
  }

  const unit = normalizeUnitOfMeasure(row.unitOfMeasure);
  return { value: row.value, unit };
}

/**
 * An odometer reading as one checklist line.
 *
 * Grouped, unlike the other values: an odometer is a large figure and unreadable without it.
 */
export function formatOdometerValue(
  row: LiveDataRow | undefined,
  language: string,
  decimals: number = DEFAULT_ODOMETER_DECIMALS,
): string {
  const odometer = odometerValue(row);
  return odometer ? formatOdometerNumber(odometer.value, odometer.unit, language, decimals) : "";
}

/**
 * An odometer number with its unit.
 *
 * Separate from {@link formatOdometerValue} because the fleet-wide view holds the number and the
 * unit apart -- the grid sorts on the number -- and both places have to read the same.
 *
 * @param value - The odometer, already in `unit`. `null` where it was never reported.
 * @param decimals - Always shown, so a column of odometers lines up.
 */
export function formatOdometerNumber(
  value: number | null,
  unit: string,
  language: string,
  decimals: number = DEFAULT_ODOMETER_DECIMALS,
): string {
  if (value === null) {
    return "-";
  }

  const number = formatNumber(value, language, { precision: decimals, fixed: true, missing: "" });
  return unit ? `${number} ${unit}` : number;
}

/**
 * A serial number exactly as reported.
 *
 * Never run through a number formatter, and never converted: that would group the digits and drop
 * leading zeros, and a serial is an identifier rather than a quantity.
 */
export function formatSerialValue(row: LiveDataRow | undefined): string {
  return row ? String(row.value) : "";
}
